import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Combobox,
  Option,
  Button,
  ButtonSize,
  ButtonColor,
  Table,
  TableRow,
} from '@code/innovance-react-ui-kit';

import { Controller, useForm } from 'react-hook-form';
import {
  Container,
  WarningText,
  FormRow,
  FormElement,
  Title,
  Subtitle,
  Separator,
  TableWrapper,
} from 'src/front/components/FormStyles';

import CustomerSearchForm, {
  SearchFormData,
  customerSearchDefaultValues,
} from 'src/front/components/CustomerSearchForm';

import {
  useCustomerSearchDispatch,
  useSetClientInfoDispatch,
  useCustomerSearchState,
  useClearDispatch,
} from 'src/front/contexts/UploadDocumentContext';

import { Customer, DocumentUpload, Credit } from 'src/front/api/apiInstance';
import {
  DocumentUploadCardListData,
  ReferenceDataType,
  SearchCreditResponseData,
} from 'src/front/api/generated';

import { dictionaryLineFormat } from 'src/front/common/utils/formUtils';
import usePRM from 'src/front/common/hooks/usePRM';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import Datepicker from 'src/front/components/Datepicker';
import { useUserInfoState } from 'src/front/contexts/SessionContext';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { UploadDocumentCreditSchema } from 'src/front/common/utils/validationSchemas';
import { TABLE_WIDTHS } from 'src/front/constant';

const customerTableHeaders = [
  'cardEntry_customerInfo_name_label',
  'cardEntry_customerInfo_middleName_label',
  'cardEntry_customerTable_birth_title',
  'cardEntry_customerInfo_gender_label',
  'cardEntry_customerTable_document_title',
];

const cardTableHeaders = [
  'uploadDocuments_cardNumber_text',
  'uploadDocuments_embossedName_text',
  'general_status_label',
  'uploadDocuments_cardExpireDate_text',
  'uploadDocuments_cardAccount_text',
];
const creditTableHeaders = [
  'general_status_label',
  'general_date_label',
  'callCenter_internetBanking_rbsAgreement_contractNumber_text',
  'general_currency_label',
  'general_components_fullName_text',
  'general_components_amount_text',
  'general_comment_label',
  'cardEntry_cardInfo_appDetail_promotionName_label',
];
const UPLOAD_DOCUMENT_TYPES = {
  CARD: 'CARD',
  CUSTOMER: 'CUSTOMER',
  CREDIT: 'CREDIT',
};

interface Props {
  goToNextStep: () => void;
}
export interface CustomerTableRow extends TableRow {
  customerNumber?: string;
}
export interface CreditTableRow extends TableRow {
  creditType?: string;
}
export interface UploadDocumentPageData {
  beginDate: Date | null;
  endDate: Date | null;
  creditStats: Option | null;
  documentType: Option | null;
}
const defaultPageValues = {
  beginDate: null,
  endDate: null,
  creditStats: null,
  documentType: null,
};
const UploadDocumentSearch: FC<Props> = ({ goToNextStep }) => {
  const customerSearchDispatch = useCustomerSearchDispatch();
  const clientInfoDispatch = useSetClientInfoDispatch();
  const clearCustomerSearch = useClearDispatch();
  const {
    documentTypeValue,
    customerSearchValues,
    customerPageNumber: initialCustomerPage,
    cardPageNumber: initialCardPage,
    creditPageNumber: initialCreditPage,
    creditInfo,
  } = useCustomerSearchState();
  const creditStats = usePRM(ReferenceDataType.CREDITSTATUS);
  const { t } = useTranslation();
  const resolver = useYupResolver(UploadDocumentCreditSchema, { todayMaxControl: true });

  const [searchFormValues, setSearchFormValues] = useState<SearchFormData>(
    customerSearchValues || customerSearchDefaultValues
  );

  const { processDate } = useUserInfoState();

  const [customerList, setCustomerList] = useState<CustomerTableRow[]>();
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerTableRow | undefined>(
    customerSearchValues?.selectedCustomer || undefined
  );
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);
  const [showCreditTable, setShowCreditTable] = useState<boolean>(false);
  const [creditLoading, setCreditLoading] = useState<boolean>(false);
  const [customerPage, setCustomerPage] = useState<number>(initialCustomerPage || 1);
  const [hasCustomerNextPage, setHasCustomerNextPage] = useState<boolean>(false);

  const [cardList, setCardList] = useState<DocumentUploadCardListData[]>([]);

  const [selectedCard, setSelectedCard] = useState<TableRow | undefined>(
    customerSearchValues?.selectedCard || undefined
  );
  const [selectedCredit, setSelectedCredit] = useState<CreditTableRow | undefined>(
    customerSearchValues?.selectedCredit || undefined
  );
  const [cardPage, setCardPage] = useState<number>(initialCardPage || 1);
  const [hasCardNextPage, setHasCardNextPage] = useState<boolean>(false);
  const [hasCreditNextPage, setHasCreditNextPage] = useState<boolean>(false);
  const [cardsLoading, setCardsLoading] = useState<boolean>(false);

  const [warning, setWarning] = useState<string | undefined>('');
  const [customerWarning, setCustomerWarning] = useState<string | undefined>('');
  const [creditList, setCreditList] = useState<SearchCreditResponseData[]>([]);
  const [creditPage, setCreditPage] = useState<number>(initialCreditPage || 1);
  const documentTypes = usePRM(ReferenceDataType.UPLOADDOCUMENTPAGETYPE);

  const { errors, getValues, control, handleSubmit, setValue, watch, register } = useForm<
    UploadDocumentPageData
  >({
    defaultValues: creditInfo || {
      ...defaultPageValues,
      beginDate: processDate || null,
      endDate: processDate || null,
    },
    resolver,
  });

  const isCardType = watch('documentType')?.value === UPLOAD_DOCUMENT_TYPES.CARD;

  const registerFields = useCallback(() => {
    register('documentType');
  }, [register]);
  useEffect(() => {
    registerFields();
  }, [registerFields]);

  useEffect(() => {
    if (documentTypes.length && customerSearchValues?.selectedCredit) {
      setValue('documentType', documentTypes[1]);
    } else if (documentTypes.length && customerSearchValues?.selectedCustomer === undefined) {
      setValue('documentType', documentTypes[0]);
    }
  }, [customerSearchValues, documentTypes, setValue]);

  const loadCustomerList = useCallback(
    async (
      pageNumber = customerPage,
      forceRefresh = false,
      formValues: SearchFormData = searchFormValues
    ): Promise<void> => {
      try {
        setCustomersLoading(true);
        const { birthDate, ...rest } = formValues;
        const { data } = await Customer.getCustomers({
          ...rest,
          birthDate: formatDate(birthDate),
          rowCountPerPage: 5,
          pageNumber: pageNumber || customerPage,
          refresh: !pageNumber || forceRefresh,
        });

        const customers = data.customerList.map((customer) => ({
          id: customer.customerId || '',
          columns: [
            `${customer.customerName} ${customer.customerSurname}`,
            customer.customerSecondName || '',
            `${customer.birthDate || ''} - ${customer.birthPlace || ''}`,
            customer.gender || '',
            `${customer.identityDocumentSerialCode} - ${customer.identityDocumentSerialNumber}`,
          ],
          customerNumber: customer.customerNumber,
        }));
        setCustomerList(customers);
        setHasCustomerNextPage(data.hasNextPage);
        setCustomerPage(pageNumber || 1);
        setCustomersLoading(false);
        setCustomerWarning(data.warning);
      } catch (error) {
        // Error Boundary
      }
    },
    [customerPage, searchFormValues]
  );

  const loadCardList = useCallback(
    async (pageNumber = cardPage, forceRefresh = false, customer = selectedCustomer) => {
      try {
        setCardsLoading(true);

        const { data } = await DocumentUpload.getCardsByCustomerNumber({
          customerNumber: customer.customerNumber || '',
          rowCountPerPage: 5,
          pageNumber: pageNumber || cardPage,
          refresh: !pageNumber || forceRefresh,
        });
        setCardList(data.cardList);
        setHasCardNextPage(data.hasNextPage);
        setCardPage(pageNumber || 1);
        setCardsLoading(false);
      } catch (err) {
        // Error Boundary
      }
    },
    [cardPage, selectedCustomer]
  );
  const loadCreditList = useCallback(
    async (pageNumber = creditPage, forceRefresh = false) => {
      try {
        setCreditLoading(true);

        const { beginDate, endDate, creditStats: stats } = creditInfo || getValues();
        const { data } = await Credit.getCredits({
          beginDate: formatDate(beginDate),
          endDate: formatDate(endDate),
          pageNumber,
          rowCountPerPage: 5,
          creditStatus: stats?.value || '',
          refresh: forceRefresh,
        });
        setValue('beginDate', beginDate);
        setValue('endDate', endDate);
        setValue('creditStats', stats);
        setCreditList(data.creditList);
        setHasCreditNextPage(data.hasNextPage);
        setCreditPage(pageNumber || creditPage);
        setCreditLoading(false);
        setShowCreditTable(true);
      } catch (err) {
        // Error Boundary
      }
    },
    [creditInfo, creditPage, getValues, setValue]
  );
  const handleSelectCustomer = useCallback(
    async (customer: TableRow | undefined): Promise<void> => {
      clearCustomerSearch();
      setSelectedCustomer(customer);
      setSelectedCard(undefined);

      if (customer) isCardType && (await loadCardList(1, true, customer));
    },
    [clearCustomerSearch, isCardType, loadCardList]
  );

  const handleSelectCard = useCallback(
    (card: TableRow | undefined): void => {
      setSelectedCard(card);
      warning && setWarning(undefined);
    },
    [warning]
  );
  const handleSelectCredit = useCallback((credit: TableRow | undefined): void => {
    setSelectedCredit(credit);
  }, []);
  useEffect(() => {
    const loadPreviousValues = async (): Promise<void> => {
      customerSearchValues?.selectedCustomer && loadCustomerList();
      customerSearchValues?.selectedCard && loadCardList();
      customerSearchValues?.selectedCredit && loadCreditList();
      setValue('documentType', documentTypeValue || null);
    };

    if (
      !customerList &&
      !cardList.length &&
      (customerSearchValues?.selectedCustomer?.id || customerSearchValues?.selectedCredit?.id) &&
      !cardsLoading &&
      !customersLoading
    ) {
      loadPreviousValues();
    }
  }, [
    cardList,
    cardsLoading,
    customerList,
    customerSearchValues,
    customersLoading,
    documentTypeValue,
    loadCardList,
    loadCreditList,
    loadCustomerList,
    setValue,
  ]);

  const handleDocumentTypeChange = (documentType: Option | null): void => {
    setValue('documentType', documentType);
    setValue('creditStats', null);
    setSelectedCustomer(undefined);
    setSelectedCard(undefined);
    setSelectedCredit(undefined);
    setCreditList([]);
    setShowCreditTable(false);
  };

  const onSubmit = async (formData: SearchFormData): Promise<void> => {
    setSearchFormValues(formData);
    clearPreviousValues();
    await loadCustomerList(1, true, formData);
  };

  const clearPreviousValues = (): void => {
    setCustomersLoading(true);
    setCustomerList([]);
    setCardList([]);
    setCreditList([]);
    setSelectedCustomer(undefined);
    setSelectedCard(undefined);
    setCustomerWarning('');
    setWarning('');
  };

  const handleContinue = async (): Promise<void> => {
    if (isCardType && !selectedCard) {
      setWarning(t('cardEntry_productInfo_supplementary_chooseCard_label'));
    } else if (selectedCustomer) {
      const { customerName, customerSurname } = searchFormValues;
      const customerSecondName = selectedCustomer.columns[1];
      try {
        setCardsLoading(true);
        const { data } = await DocumentUpload.prepareClientInfo({
          customerSurname,
          customerName,
          customerSecondName,
          customerNumber: selectedCustomer.customerNumber || '',

          customerId: selectedCustomer.id,
          ...(isCardType ? { cardNumber: selectedCard?.columns[0] } : {}),
          uploadDocumentFormType: dictionaryLineFormat(watch('documentType')),
        });
        if (data.result === 'OK') {
          goToNextStep();
          customerSearchDispatch({
            documentTypeValue: watch('documentType'),
            customerSearchValues: {
              ...searchFormValues,
              customerNumber: selectedCustomer.customerNumber || '',
              customerSecondName,
              selectedCustomer,
              selectedCard,
            },
            customerPageNumber: customerPage,
            cardPageNumber: cardPage,
          });
          clientInfoDispatch({ clientInfo: data });
        }
      } catch (error) {
        // Error Boundary
      }
    } else if (selectedCredit) {
      setCreditLoading(true);
      const { data } = await DocumentUpload.prepareClientInfo({
        customerId: '',
        customerName: '',
        customerNumber: '',
        customerSecondName: '',
        customerSurname: '',
        creditId: selectedCredit.id,

        uploadDocumentFormType: dictionaryLineFormat(watch('documentType')),
      });
      if (data.result === 'OK') {
        const creditInfos = getValues();

        customerSearchDispatch({
          documentTypeValue: watch('documentType'),
          customerSearchValues: {
            ...searchFormValues,
            customerNumber: '',
            customerName: '',
            customerSecondName: '',
            customerSurname: '',
            selectedCredit,
          },
          creditPageNumber: creditPage,
          creditInfo: {
            documentType: creditInfos.documentType,
            creditStats: creditInfos.creditStats,
            beginDate: creditInfos.beginDate,
            endDate: creditInfos.endDate,
          },
        });
        clientInfoDispatch({ clientInfo: data });
        goToNextStep();
      }
    }
  };
  const creditSearchSubmit = async (): Promise<void> => {
    await handleCreditSearch(1, true);
  };
  const handleCreditSearch = useCallback(
    async (pageNumber?: number, forceRefresh = false): Promise<void> => {
      const { beginDate, endDate, creditStats: stats } = getValues();
      const { data } = await Credit.getCredits({
        beginDate: formatDate(beginDate),
        endDate: formatDate(endDate),
        creditStatus: stats?.value || '',
        pageNumber,
        refresh: forceRefresh,
        rowCountPerPage: 5,
      });
      if (data.result === 'OK') {
        setCreditList(data.creditList);
        setHasCreditNextPage(data.hasNextPage);
      }
      setShowCreditTable(true);
    },
    [getValues]
  );

  const cardTableData: TableRow[] = cardList.map((card) => ({
    id: card.cardId || '',
    columns: [
      card.cardNumber,
      card.cardEmbossedName,
      card.cardStatus || '',
      card.expireDate || '',
      card.cardAccountNumber,
    ],
  }));
  const creditTableData: TableRow[] = creditList.map((credit) => ({
    id: credit.creditId,
    columns: [
      credit.status,
      credit.date,
      credit.contractNumber,
      credit.currency,
      credit.fullName,
      credit.amount,
      credit.comment,
      credit.promotionName,
    ],
    creditType: credit.creditType,
  }));
  return (
    <Container>
      <Title>
        <Trans i18nKey="uploadDocuments_title" />
      </Title>
      <Separator />
      <form onSubmit={handleSubmit(creditSearchSubmit)}>
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={t(errors?.documentType?.message as string)}
              items={documentTypes}
              label={t('uploadDocuments_type_label')}
              name="documentType"
              onChange={handleDocumentTypeChange}
              value={watch('documentType')}
              required
            />
          </FormElement>
          {watch('documentType')?.value === UPLOAD_DOCUMENT_TYPES.CREDIT ? (
            <FormElement>
              <Controller
                control={control}
                name="creditStats"
                render={({ onChange, value }) => (
                  <Combobox
                    items={creditStats}
                    label={t('newObserve_status_label')}
                    name="creditStats"
                    onChange={onChange}
                    value={value}
                  />
                )}
              />
            </FormElement>
          ) : null}
        </FormRow>
        <Separator />
        {watch('documentType')?.value === 'CREDIT' || watch('documentType') === null ? (
          <>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="beginDate"
                  render={({ onChange, value }) => (
                    <Datepicker
                      errorMessage={t(errors?.beginDate?.message as string)}
                      inputLabel={`${t('general_components_startDate_label')} *`}
                      onChange={onChange}
                      value={value as Date}
                    />
                  )}
                />
              </FormElement>
              <FormElement>
                <Controller
                  control={control}
                  name="endDate"
                  render={({ onChange, value }) => (
                    <Datepicker
                      errorMessage={t(errors?.endDate?.message as string)}
                      inputLabel={`${t('general_components_endDate_label')} *`}
                      onChange={onChange}
                      value={value as Date}
                    />
                  )}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement />
              <Button
                color={ButtonColor.Primary}
                size={ButtonSize.Medium}
                text={t('general_components_search_button')}
                type="submit"
              />
            </FormRow>

            <Separator />
            {creditList.length || showCreditTable ? (
              <TableWrapper>
                <Table
                  emptyTableText={t('general_components_notFound_errorMessage')}
                  hasNextPage={hasCreditNextPage}
                  headers={creditTableHeaders.map((header) => t(header))}
                  items={creditTableData}
                  loading={creditLoading}
                  loadPageItems={loadCreditList}
                  onSelect={handleSelectCredit}
                  pageNumber={creditPage}
                  selectedItem={selectedCredit}
                  tableWidth={TABLE_WIDTHS.DESKTOP_2X}
                  widths={['16', '10', '16', '8', '12', '12', '12', '14']}
                  clearItemOnPageChange
                  withRadio
                />
              </TableWrapper>
            ) : null}
            {selectedCredit ? (
              <Button
                color={ButtonColor.Primary}
                onClick={handleContinue}
                size={ButtonSize.Medium}
                text={t('general_continue_button')}
                type="button"
              />
            ) : null}
          </>
        ) : (
          <>
            <Subtitle>
              <Trans i18nKey="uploadDocuments_customerInfo_text" />
            </Subtitle>
            <CustomerSearchForm formValues={searchFormValues} onSubmit={onSubmit} />
            {customerList ? (
              <>
                <Separator />
                <Subtitle>
                  <Trans i18nKey="uploadDocuments_clientList_text" />
                </Subtitle>
                <TableWrapper>
                  <Table
                    emptyTableText={t('cardEntry_customerInfo_notFound_text')}
                    hasNextPage={hasCustomerNextPage}
                    headers={customerTableHeaders.map((head) => t(head))}
                    items={customerList}
                    loading={customersLoading}
                    loadPageItems={loadCustomerList}
                    onSelect={handleSelectCustomer}
                    pageNumber={customerPage}
                    selectedItem={selectedCustomer}
                    widths={['18', '18', '30', '12', '22']}
                    clearItemOnPageChange
                    withRadio
                  />
                </TableWrapper>
                {customerWarning ? <WarningText>{customerWarning}</WarningText> : null}

                {selectedCustomer && isCardType ? (
                  <>
                    <Separator />
                    <Subtitle>
                      <Trans i18nKey="uploadDocuments_cardList_text" />
                    </Subtitle>
                    <TableWrapper>
                      <Table
                        emptyTableText={t('cardEntry_productInfo_supplementary_noCardFound_label')}
                        hasNextPage={hasCardNextPage}
                        headers={cardTableHeaders.map((head) => t(head))}
                        items={cardTableData}
                        loading={cardsLoading}
                        loadPageItems={loadCardList}
                        onSelect={handleSelectCard}
                        pageNumber={cardPage}
                        selectedItem={selectedCard}
                        widths={['23', '18', '13', '18', '28']}
                        clearItemOnPageChange
                        withRadio
                      />
                    </TableWrapper>
                    {warning ? <WarningText>{warning}</WarningText> : null}
                    <Separator />
                  </>
                ) : null}
                {selectedCustomer ? (
                  <Button
                    color={ButtonColor.Primary}
                    onClick={handleContinue}
                    size={ButtonSize.Medium}
                    text={t('general_continue_button')}
                    type="button"
                  />
                ) : null}
              </>
            ) : null}
          </>
        )}
      </form>
    </Container>
  );
};

export default UploadDocumentSearch;
